import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Contact2, Search, Plus, Trash2, Pencil, Upload, Download, Music, Briefcase, FileText, History, MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCategoriesForKind } from "@/constants/categories";

const KINDS = [
  { v: "all",         label: "Tous" },
  { v: "artist",      label: "Artistes", Icon: Music },
  { v: "prestataire", label: "Prestataires", Icon: Briefcase },
];

const EMPTY_CONTACT = {
  kind: "prestataire", first_name: "", last_name: "", stage_name: "", company: "",
  category: "", phone: "", email: "", address: "", notes: "",
};

const EMPTY_ITEM = {
  kind: "prestation", name: "", duration: "", setup_time: "", teardown_time: "",
  declaration_or_facture: null, cost: "", travel_costs: "", comment: "",
};

const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== "all") params.kind = kind;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/contacts", { params });
      setItems(data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  // Détection de doublons à la volée (case-insensitive) sur la catégorie
  // en cours. Match si :
  //  - Artiste : nom de scène identique OU email identique OU (prénom+nom) identiques
  //  - Prestataire : entreprise identique OU email identique OU (prénom+nom) identiques
  const norm = (s) => (s || "").trim().toLowerCase();
  const duplicateContact = (() => {
    const email = norm(form.email);
    const first = norm(form.first_name);
    const last = norm(form.last_name);
    const stage = norm(form.stage_name);
    const company = norm(form.company);
    if (!email && !stage && !company && !(first && last)) return null;
    return items.find(c => {
      if (c.kind !== form.kind) return false;
      if (email && norm(c.email) === email) return true;
      if (form.kind === "artist" && stage && norm(c.stage_name) === stage) return true;
      if (form.kind === "prestataire" && company && norm(c.company) === company) return true;
      if (first && last && norm(c.first_name) === first && norm(c.last_name) === last) return true;
      return false;
    }) || null;
  })();

  const createContact = async () => {
    if (!form.first_name && !form.last_name && !form.company && !form.stage_name) {
      toast.error("Renseignez au moins un nom / nom d'entreprise / nom de scène");
      return;
    }
    if (duplicateContact) {
      toast.error(`Cette fiche existe déjà : « ${duplicateContact.display_name || duplicateContact.stage_name || duplicateContact.company || `${duplicateContact.first_name} ${duplicateContact.last_name}`} ». Ouvrez-la pour la modifier.`);
      return;
    }
    try {
      const { data } = await api.post("/contacts", form);
      toast.success("Fiche créée");
      setForm(EMPTY_CONTACT);
      setOpenCreate(false);
      await load();
      setOpenId(data.id);
    } catch (e) { toast.error(extractError(e)); }
  };

  const openDetail = openId ? items.find(c => c.id === openId) : null;

  return (
    <div className="space-y-4" data-testid="contacts-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Activité</div>
          <h1 className="font-brand text-2xl md:text-3xl leading-tight flex items-center gap-2">
            <Contact2 className="h-6 w-6 text-primary" /> Prestataires & Artistes
          </h1>
        </div>
        <Button className="rounded-full bg-primary active:scale-95" onClick={() => { setForm(EMPTY_CONTACT); setOpenCreate(true); }} data-testid="new-contact-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle fiche
        </Button>
      </div>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") load(); }}
                placeholder="Rechercher par nom, société, catégorie, email…"
                className="w-full h-9 pl-9 pr-3 rounded-full border border-border bg-background text-sm"
                data-testid="contacts-search"
              />
            </div>
            <div className="flex items-center gap-1 rounded-full bg-muted p-1">
              {KINDS.map(k => (
                <button
                  key={k.v}
                  onClick={() => setKind(k.v)}
                  className={`px-3 h-7 rounded-full text-xs font-semibold flex items-center gap-1 ${kind === k.v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`filter-${k.v}`}
                >
                  {k.Icon && <k.Icon className="h-3 w-3" />}
                  {k.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center text-muted-foreground py-10">Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-border/60"><CardContent className="p-10 text-center text-muted-foreground">Aucun contact — commencez par créer une fiche.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(c => {
            const isArtist = c.kind === "artist";
            const KIcon = isArtist ? Music : Briefcase;
            return (
              <button
                key={c.id}
                onClick={() => setOpenId(c.id)}
                className="text-left rounded-2xl border border-border/60 bg-card hover:border-primary/60 hover:shadow-warm transition p-4"
                data-testid={`contact-card-${c.id}`}
                title="Cliquer pour ouvrir et modifier la fiche"
              >
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <KIcon className="h-3 w-3" />
                  <span className="uppercase tracking-widest font-bold">{isArtist ? "Artiste" : "Prestataire"}</span>
                  {c.category && <Badge variant="outline" className="ml-auto text-[10px] rounded-full">{c.category}</Badge>}
                </div>
                <div className="font-brand text-lg font-semibold truncate">{c.display_name}</div>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {c.email && <div className="truncate flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</div>}
                  {c.phone && <div className="truncate flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</div>}
                  {c.address && <div className="truncate flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.address}</div>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-2">
                  {(c.items?.length || 0)} {isArtist ? "numéros" : "prestations"} · {(c.documents?.length || 0)} documents
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog création */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg" data-testid="new-contact-dialog">
          <DialogHeader><DialogTitle>Nouvelle fiche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Catégorie *</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setForm(f => ({ ...f, kind: "artist" }))}
                  className={`flex-1 rounded-xl border px-3 py-2 text-sm ${form.kind === "artist" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  <Music className="h-4 w-4 inline mr-1" /> Artiste
                </button>
                <button type="button" onClick={() => setForm(f => ({ ...f, kind: "prestataire" }))}
                  className={`flex-1 rounded-xl border px-3 py-2 text-sm ${form.kind === "prestataire" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  <Briefcase className="h-4 w-4 inline mr-1" /> Prestataire
                </button>
              </div>
            </div>
            {form.kind === "artist" && (
              <div><Label className="text-xs">Nom de scène</Label><Input value={form.stage_name} onChange={e => setForm(f => ({ ...f, stage_name: e.target.value }))} data-testid="new-stage-name" /></div>
            )}
            {form.kind === "prestataire" && (
              <div><Label className="text-xs">Entreprise</Label><Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} data-testid="new-company" /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Prénom</Label><Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></div>
              <div><Label className="text-xs">Nom</Label><Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs">Catégorie / type</Label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm"
                data-testid="new-contact-category"
              >
                <option value="">— Choisir une catégorie —</option>
                {getCategoriesForKind(form.kind).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Téléphone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            {duplicateContact && (
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 text-destructive text-xs px-3 py-2 flex items-start gap-2"
                data-testid="contact-duplicate-warning"
              >
                <Contact2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <b>Cette fiche existe déjà</b> : «&nbsp;{duplicateContact.display_name || duplicateContact.stage_name || duplicateContact.company || `${duplicateContact.first_name || ""} ${duplicateContact.last_name || ""}`.trim()}&nbsp;»
                  {duplicateContact.category ? ` — ${duplicateContact.category}` : ""}
                  {duplicateContact.email ? ` · ${duplicateContact.email}` : ""}.
                  <button
                    type="button"
                    className="underline ml-1 font-semibold hover:opacity-80"
                    onClick={() => { setOpenCreate(false); setTimeout(() => setOpenId(duplicateContact.id), 60); }}
                    data-testid="contact-duplicate-open-existing"
                  >
                    Ouvrir la fiche existante
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpenCreate(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-primary disabled:opacity-40"
              onClick={createContact}
              disabled={!!duplicateContact}
              data-testid="new-contact-submit"
            >
              <Plus className="h-4 w-4 mr-2" /> Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog détail / édition */}
      <Dialog open={!!openId} onOpenChange={v => { if (!v) setOpenId(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="contact-detail-dialog">
          {openDetail && <ContactDetail contact={openDetail} onClose={() => setOpenId(null)} reload={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactDetail({ contact: initial, onClose, reload }) {
  const [c, setC] = useState(initial);
  const [tab, setTab] = useState("infos");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const refreshContact = async () => {
    const { data } = await api.get(`/contacts/${c.id}`);
    setC(data);
    reload();
  };
  useEffect(() => { setC(initial); }, [initial.id]);
  useEffect(() => {
    if (tab === "history") {
      api.get(`/contacts/${c.id}/history`).then(r => setHistory(r.data)).catch(() => setHistory([]));
    }
  }, [tab, c.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/contacts/${c.id}`, {
        kind: c.kind, first_name: c.first_name, last_name: c.last_name,
        stage_name: c.stage_name, company: c.company, category: c.category,
        phone: c.phone, email: c.email, address: c.address, notes: c.notes,
      });
      toast.success("Enregistré");
      await refreshContact();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm(`Supprimer définitivement la fiche « ${c.display_name || c.first_name} » ?`)) return;
    try { await api.delete(`/contacts/${c.id}`); toast.success("Fiche supprimée"); onClose(); reload(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const isArtist = c.kind === "artist";
  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {isArtist ? <Music className="h-5 w-5 text-primary" /> : <Briefcase className="h-5 w-5 text-primary" />}
          {c.display_name || (isArtist ? "Nouvel artiste" : "Nouveau prestataire")}
          {c.category && <Badge variant="outline" className="ml-2 rounded-full">{c.category}</Badge>}
        </DialogTitle>
      </DialogHeader>

      <div className="flex items-center gap-1 rounded-full bg-muted p-1 w-fit text-xs">
        {[
          { v: "infos", label: "Infos" },
          { v: "items", label: isArtist ? "Numéros / spectacles" : "Prestations" },
          { v: "docs",  label: "Documents" },
          { v: "history", label: "Historique devis", Icon: History },
        ].map(t => (
          <button key={t.v} onClick={() => setTab(t.v)}
            className={`px-3 h-7 rounded-full font-semibold flex items-center gap-1 ${tab === t.v ? "bg-primary text-white" : "text-muted-foreground"}`}
            data-testid={`contact-tab-${t.v}`}>
            {t.Icon && <t.Icon className="h-3 w-3" />} {t.label}
          </button>
        ))}
      </div>

      {tab === "infos" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {isArtist ? (
            <div className="md:col-span-2"><Label className="text-xs">Nom de scène</Label><Input value={c.stage_name || ""} onChange={e => setC({ ...c, stage_name: e.target.value })} /></div>
          ) : (
            <div className="md:col-span-2"><Label className="text-xs">Entreprise</Label><Input value={c.company || ""} onChange={e => setC({ ...c, company: e.target.value })} /></div>
          )}
          <div><Label className="text-xs">Prénom</Label><Input value={c.first_name || ""} onChange={e => setC({ ...c, first_name: e.target.value })} /></div>
          <div><Label className="text-xs">Nom</Label><Input value={c.last_name || ""} onChange={e => setC({ ...c, last_name: e.target.value })} /></div>
          <div><Label className="text-xs">Catégorie / type</Label>
            <select
              value={c.category || ""}
              onChange={e => setC({ ...c, category: e.target.value })}
              className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm"
              data-testid="edit-contact-category"
            >
              <option value="">— Choisir une catégorie —</option>
              {getCategoriesForKind(c.kind).map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Téléphone</Label><Input value={c.phone || ""} onChange={e => setC({ ...c, phone: e.target.value })} /></div>
          <div><Label className="text-xs">Email</Label><Input value={c.email || ""} onChange={e => setC({ ...c, email: e.target.value })} /></div>
          <div className="md:col-span-2"><Label className="text-xs">Adresse</Label><Input value={c.address || ""} onChange={e => setC({ ...c, address: e.target.value })} /></div>
          <div className="md:col-span-2"><Label className="text-xs">Notes</Label>
            <textarea className="w-full h-24 rounded-md border border-border bg-background p-2 text-sm" value={c.notes || ""} onChange={e => setC({ ...c, notes: e.target.value })} />
          </div>
        </div>
      )}

      {tab === "items" && <ItemsSection contact={c} refresh={refreshContact} />}
      {tab === "docs" && <DocsSection contact={c} refresh={refreshContact} />}
      {tab === "history" && <HistorySection history={history} />}

      <DialogFooter>
        <Button variant="ghost" className="text-destructive rounded-full" onClick={remove} data-testid="contact-delete"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
        <Button variant="outline" className="rounded-full" onClick={onClose}>Fermer</Button>
        {tab === "infos" && (
          <Button className="rounded-full bg-primary" onClick={save} disabled={saving} data-testid="contact-save"><Pencil className="h-4 w-4 mr-2" /> Enregistrer</Button>
        )}
      </DialogFooter>
    </div>
  );
}

function ItemsSection({ contact, refresh }) {
  const isArtist = contact.kind === "artist";
  const [form, setForm] = useState({ ...EMPTY_ITEM, kind: isArtist ? "numero" : "prestation" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);       // id de l'item en cours d'édition
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const addItem = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, kind: isArtist ? "numero" : "prestation",
        cost: parseFloat(form.cost) || 0, travel_costs: parseFloat(form.travel_costs) || 0 };
      await api.post(`/contacts/${contact.id}/items`, payload);
      toast.success("Ajouté");
      setForm({ ...EMPTY_ITEM, kind: isArtist ? "numero" : "prestation" });
      refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer cet élément ?")) return;
    try { await api.delete(`/contacts/${contact.id}/items/${id}`); refresh(); } catch (e) { toast.error(extractError(e)); }
  };
  const uploadTechSheet = async (itemId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/contacts/${contact.id}/items/${itemId}/tech-sheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fiche technique importée"); refresh();
    } catch (e) { toast.error(extractError(e)); }
  };
  const startEdit = (it) => {
    setEditingId(it.id);
    setEditForm({
      name: it.name || "",
      duration: it.duration || "",
      setup_time: it.setup_time || "",
      teardown_time: it.teardown_time || "",
      cost: it.cost ?? "",
      travel_costs: it.travel_costs ?? "",
      declaration_or_facture: it.declaration_or_facture || "",
      comment: it.comment || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(null); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Nom requis"); return; }
    setEditSaving(true);
    try {
      const patch = {
        name: editForm.name.trim(),
        duration: editForm.duration || "",
        setup_time: editForm.setup_time || "",
        teardown_time: editForm.teardown_time || "",
        cost: editForm.cost === "" ? 0 : parseFloat(editForm.cost),
        travel_costs: editForm.travel_costs === "" ? 0 : parseFloat(editForm.travel_costs),
        declaration_or_facture: editForm.declaration_or_facture || null,
        comment: editForm.comment || "",
      };
      await api.patch(`/contacts/${contact.id}/items/${editingId}`, patch);
      toast.success(isArtist ? "Numéro modifié" : "Prestation modifiée");
      cancelEdit();
      refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setEditSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="border border-dashed border-primary/40 rounded-xl bg-primary/5 p-3 space-y-2">
        <div className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-1">
          <Plus className="h-3 w-3" /> Ajouter {isArtist ? "un numéro / spectacle" : "une prestation"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <Input placeholder={isArtist ? "Nom du numéro" : "Nom de la prestation"} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="item-name" />
          {isArtist && <Input placeholder="Durée (ex: 45 min)" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} />}
          <Input placeholder="Temps de montage" value={form.setup_time} onChange={e => setForm({ ...form, setup_time: e.target.value })} />
          <Input placeholder="Temps de démontage" value={form.teardown_time} onChange={e => setForm({ ...form, teardown_time: e.target.value })} />
          <Input type="number" step="0.01" placeholder="Coût (€)" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} />
          <Input type="number" step="0.01" placeholder="Frais de déplacement (€)" value={form.travel_costs} onChange={e => setForm({ ...form, travel_costs: e.target.value })} />
          {isArtist && (
            <select value={form.declaration_or_facture || ""} onChange={e => setForm({ ...form, declaration_or_facture: e.target.value || null })}
              className="h-10 rounded-md border border-border bg-background px-2 text-sm md:col-span-2">
              <option value="">Déclaration ou facture ?</option>
              <option value="declaration">Déclaration</option>
              <option value="facture">Facture</option>
            </select>
          )}
          <Input placeholder="Commentaire" value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} className="md:col-span-2" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" className="rounded-full bg-primary" onClick={addItem} disabled={saving} data-testid="item-add"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
        </div>
      </div>

      <div className="divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
        {(contact.items || []).length === 0 ? (
          <div className="text-sm text-muted-foreground italic text-center py-6">Aucun {isArtist ? "numéro" : "prestation"} enregistré.</div>
        ) : contact.items.map(it => (
          <div key={it.id} className="p-3 space-y-1" data-testid={`item-row-${it.id}`}>
            {editingId === it.id ? (
              <div className="space-y-2" data-testid={`item-edit-${it.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Modifier {isArtist ? "le numéro" : "la prestation"}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <Input placeholder={isArtist ? "Nom du numéro" : "Nom de la prestation"} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid={`item-edit-name-${it.id}`} />
                  {isArtist && <Input placeholder="Durée (ex: 45 min)" value={editForm.duration} onChange={e => setEditForm(f => ({ ...f, duration: e.target.value }))} data-testid={`item-edit-duration-${it.id}`} />}
                  <Input placeholder="Temps de montage" value={editForm.setup_time} onChange={e => setEditForm(f => ({ ...f, setup_time: e.target.value }))} data-testid={`item-edit-setup-${it.id}`} />
                  <Input placeholder="Temps de démontage" value={editForm.teardown_time} onChange={e => setEditForm(f => ({ ...f, teardown_time: e.target.value }))} data-testid={`item-edit-teardown-${it.id}`} />
                  <Input type="number" step="0.01" placeholder="Coût (€)" value={editForm.cost} onChange={e => setEditForm(f => ({ ...f, cost: e.target.value }))} data-testid={`item-edit-cost-${it.id}`} />
                  <Input type="number" step="0.01" placeholder="Frais de déplacement (€)" value={editForm.travel_costs} onChange={e => setEditForm(f => ({ ...f, travel_costs: e.target.value }))} data-testid={`item-edit-travel-${it.id}`} />
                  {isArtist && (
                    <select value={editForm.declaration_or_facture || ""} onChange={e => setEditForm(f => ({ ...f, declaration_or_facture: e.target.value || null }))}
                      className="h-10 rounded-md border border-border bg-background px-2 text-sm md:col-span-2" data-testid={`item-edit-payment-${it.id}`}>
                      <option value="">Déclaration ou facture ?</option>
                      <option value="declaration">Déclaration</option>
                      <option value="facture">Facture</option>
                    </select>
                  )}
                  <Input placeholder="Commentaire" value={editForm.comment} onChange={e => setEditForm(f => ({ ...f, comment: e.target.value }))} className="md:col-span-2" data-testid={`item-edit-comment-${it.id}`} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={cancelEdit}>Annuler</Button>
                  <Button size="sm" className="rounded-full bg-primary" onClick={saveEdit} disabled={editSaving} data-testid={`item-edit-save-${it.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> {editSaving ? "Enregistrement…" : "Enregistrer"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm flex-1">{it.name}</div>
                  <div className="text-sm font-bold tabular-nums text-primary shrink-0">{Number(it.cost || 0).toLocaleString("fr-FR")} €</div>
                  <Button variant="ghost" size="icon" className="text-primary h-8 w-8" onClick={() => startEdit(it)} data-testid={`item-edit-btn-${it.id}`} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => del(it.id)} title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                  {it.duration && <span>Durée : {it.duration}</span>}
                  {it.setup_time && <span>Montage : {it.setup_time}</span>}
                  {it.teardown_time && <span>Démontage : {it.teardown_time}</span>}
                  {it.travel_costs > 0 && <span>Frais dépl. : {Number(it.travel_costs).toLocaleString("fr-FR")} €</span>}
                  {it.declaration_or_facture && <span>{it.declaration_or_facture === "declaration" ? "Déclaration" : "Facture"}</span>}
                </div>
                {it.comment && <div className="text-xs italic text-muted-foreground">« {it.comment} »</div>}
                <div className="flex items-center gap-2 pt-1">
                  {it.tech_sheet_file_id ? (
                    <a href={`${apiBase}/files/${it.tech_sheet_file_id}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline flex items-center gap-1"><FileText className="h-3 w-3" /> {it.tech_sheet_name || "Fiche technique"}</a>
                  ) : (
                    <label className="text-xs cursor-pointer inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1">
                      <Upload className="h-3 w-3" /> Fiche technique (PDF)
                      <input type="file" accept="application/pdf" className="hidden" onChange={e => uploadTechSheet(it.id, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DocsSection({ contact, refresh }) {
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!name.trim()) { toast.error("Nom du document requis"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("name", name.trim());
      await api.post(`/contacts/${contact.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté"); setName(""); e.target.value = ""; refresh();
    } catch (err) { toast.error(extractError(err)); }
    finally { setUploading(false); }
  };
  const del = async (docId) => {
    if (!confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/contacts/${contact.id}/documents/${docId}`); refresh(); } catch (e) { toast.error(extractError(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="border border-dashed border-primary/40 rounded-xl bg-primary/5 p-3 flex flex-col md:flex-row gap-2 items-end">
        <div className="flex-1"><Label className="text-xs">Nom du document *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex : CV, KBIS, contrat type…" /></div>
        <label className={`inline-flex items-center gap-2 rounded-full ${uploading || !name.trim() ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-primary text-white cursor-pointer active:scale-95"} px-4 h-9 text-sm font-semibold`}>
          <Upload className="h-4 w-4" /> {uploading ? "Envoi…" : "Ajouter"}
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} disabled={uploading || !name.trim()} />
        </label>
      </div>
      <div className="divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
        {(contact.documents || []).length === 0 ? (
          <div className="text-sm text-muted-foreground italic text-center py-6">Aucun document.</div>
        ) : contact.documents.map(d => (
          <div key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted" data-testid={`contact-doc-${d.id}`}>
            <FileText className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{d.name}</div>
              <div className="text-[11px] text-muted-foreground">{d.original_name} · {Math.round((d.size || 0) / 1024)} Ko · {(d.uploaded_at || "").slice(0, 10)}</div>
            </div>
            <a href={`${apiBase}/files/${d.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline flex items-center gap-1"><Download className="h-3 w-3" /> Ouvrir</a>
            <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => del(d.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistorySection({ history }) {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
  if (!history.length) return <div className="text-sm text-muted-foreground italic text-center py-6">Aucun devis reçu pour ce contact.</div>;
  return (
    <div className="divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
      {history.map(h => (
        <div key={h.event_id} className="px-3 py-3 hover:bg-muted space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/evenements/${h.event_id}?tab=commerc`)}
                className="text-sm font-semibold truncate text-primary hover:underline text-left inline-flex items-center gap-1"
                data-testid={`history-event-link-${h.event_id}`}
              >
                {h.event_name} <ExternalLink className="h-3 w-3" />
              </button>
              <span className="text-muted-foreground font-normal text-sm"> — {h.client || "—"}</span>
              <div className="text-[11px] text-muted-foreground tabular-nums">{h.start_date ? fmtFR(h.start_date) : "—"} · Demande {h.request_date ? fmtFR(h.request_date) : "?"}</div>
            </div>
            <div className="text-sm font-bold tabular-nums shrink-0">{h.offer_amount ? `${Number(h.offer_amount).toLocaleString("fr-FR")} €` : "—"}</div>
            {h.offer_decision === "validated" ? (
              <Badge className="rounded-full bg-emerald-500/15 text-emerald-300 border-emerald-500/50 text-[10px]">Validée</Badge>
            ) : h.offer_decision === "refused" ? (
              <Badge className="rounded-full bg-rose-500/15 text-rose-300 border-rose-500/50 text-[10px]">Refusée</Badge>
            ) : h.received ? (
              <Badge variant="outline" className="rounded-full text-muted-foreground text-[10px]">Reçue</Badge>
            ) : (
              <Badge variant="outline" className="rounded-full text-secondary-foreground text-[10px]">En attente</Badge>
            )}
          </div>
          {h.files && h.files.length > 0 && (
            <div className="pl-2 space-y-1">
              {h.files.map(f => (
                <a key={f.id} href={`${apiBase}/files/${f.file_id}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-xs text-primary hover:underline"
                  data-testid={`history-file-${f.id}`}>
                  <FileText className="h-3 w-3" /> {f.name}
                  <span className="text-muted-foreground">· {Math.round((f.size||0)/1024)} Ko</span>
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
